import { jsx as _jsx } from "react/jsx-runtime";
/**
 * PricingProvider — React context that holds the Hub API base URL
 * and shared catalog cache for all usePricing() consumers.
 *
 * Must be nested inside <AuthProvider> so useAuth() is available.
 *
 * @example
 * ```tsx
 * import { PricingProvider } from "@d-m-organization/pricing-ui";
 *
 * function App() {
 *   return (
 *     <AuthProvider>
 *       <PricingProvider apiBase={import.meta.env.VITE_HUB_API_BASE}>
 *         <MyRoutes />
 *       </PricingProvider>
 *     </AuthProvider>
 *   );
 * }
 * ```
 */
import { createContext, useContext, useMemo } from "react";
import { TtlCache } from "./cache.js";
const PricingContext = createContext(null);
// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------
const CATALOG_CACHE_TTL_MS = 60_000; // 60 seconds, matches server Cache-Control
export function PricingProvider({ apiBase, children }) {
    // Stable cache instance — survives re-renders, shared across all hooks
    const catalogCache = useMemo(() => new TtlCache(CATALOG_CACHE_TTL_MS), []);
    const value = useMemo(() => ({ apiBase, catalogCache }), [apiBase, catalogCache]);
    return (_jsx(PricingContext.Provider, { value: value, children: children }));
}
// ---------------------------------------------------------------------------
// Internal hook for accessing context
// ---------------------------------------------------------------------------
export function usePricingContext() {
    const ctx = useContext(PricingContext);
    if (!ctx) {
        throw new Error("usePricing() must be used inside <PricingProvider>. " +
            "Wrap your app with <PricingProvider apiBase=\"...\">.");
    }
    return ctx;
}
